/**
 * Hands an old song to Parliament Polish from anywhere in the app (Vault row, Sessions row).
 *
 * A module-level store rather than a React context because the producers (Vault, Sessions) and the
 * consumer (the Polish box inside the Studio tab) live in different tabs, and App itself needs the
 * header it holds when a polish is accepted. Nothing here is persisted; a reload clears it.
 */

import { useSyncExternalStore } from 'react';
import { splitStudioTeamScaffold } from '@/shared/studioTeamScaffold';

export interface PolishHandoff {
  /** The lyrics to put in the Polish box. */
  lyrics: string;
  /** Studio Team cue/dossier header to put back after polishing ('' when the song has none). */
  scaffold: string;
  title: string;
  draftId: string | null;
  /** Changes on every send so the box reacts even when the same song is sent twice. */
  nonce: number;
}

export interface PolishSource {
  id?: string | null;
  title?: string | null;
  lyricsWithTags?: string | null;
  lyricsAlone?: string | null;
}

let current: PolishHandoff | null = null;
let armedScaffold = '';
let nonce = 0;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach(l => l());

/** Builds the handoff for a Vault song. Returns null when the song has no lyrics to polish. */
export function buildPolishHandoff(src: PolishSource): Omit<PolishHandoff, 'nonce'> | null {
  const full = (src.lyricsWithTags ?? '').trim() || (src.lyricsAlone ?? '').trim();
  if (!full) return null;
  const { scaffold, lyrics } = splitStudioTeamScaffold(full);
  if (!lyrics) return null;
  return { lyrics, scaffold, title: src.title?.trim() || 'Untitled', draftId: src.id ?? null };
}

/** Sends a song to the Polish box and switches to the Studio tab. Returns false if there was nothing to send. */
export function sendToPolish(src: PolishSource): boolean {
  const built = buildPolishHandoff(src);
  if (!built) return false;
  current = { ...built, nonce: ++nonce };
  emit();
  window.dispatchEvent(new CustomEvent('hookhouse:goto-tab', { detail: { tabId: 'studio' } }));
  return true;
}

export function getPolishHandoff(): PolishHandoff | null {
  return current;
}

/** React hook: the latest handoff, or null. */
export function usePolishHandoff(): PolishHandoff | null {
  return useSyncExternalStore(
    cb => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => current,
    () => null,
  );
}

/**
 * Called when a polish starts. The header only travels with the song it came from: if the lyrics in the
 * box were edited away from the handed-over text it would no longer match, so it is dropped.
 */
export function armScaffold(lyricsBeingPolished: string): void {
  armedScaffold = current && current.lyrics.trim() === lyricsBeingPolished.trim() ? current.scaffold : '';
}

/** Returns the header armed for the current polish and clears it. */
export function takeScaffold(): string {
  const s = armedScaffold;
  armedScaffold = '';
  return s;
}

/** Test helper. */
export function resetPolishHandoff(): void {
  current = null;
  armedScaffold = '';
  nonce = 0;
  emit();
}
